/**
 * "Se busca": the public board of cards people are looking for. A "Busco" (market_wants) made public
 * shows there with an alias and, if the owner says so, a province and a max price. Never the email.
 */
import type { MarketCondition } from "./market";
import { GAME_PARAM, gameSlug } from "./games";

/** Public "Busco" one person can have on the board at a time (of the MAX_WANTS they can save). */
export const MAX_PUBLIC_WANTS = 20;
/** A public "Busco" leaves the board this long after it was published or last moved up. */
export const WANT_PUBLIC_DAYS = 60;
/** "Subir" (move it back to the top) at most this often. */
export const WANT_BUMP_DAYS = 7;
export const WANT_NOTE_MAX = 300;
export const WANT_MAX_QUANTITY = 20;
export const NICKNAME_MIN = 2;
export const NICKNAME_MAX = 24;
/** A board post with a max price above this goes to the admin on Telegram. */
export const WANT_ALERT_PRICE_ARS = 300_000;
export const WANTED_PAGE_SIZE = 24;
export const WANTED_SORTS = ["recent", "most"] as const;
export type WantedSort = (typeof WANTED_SORTS)[number];

/** Argentina's 24 jurisdictions, for the board's province (code → name). */
export const PROVINCES = {
  caba: "Ciudad de Buenos Aires",
  "buenos-aires": "Buenos Aires",
  catamarca: "Catamarca",
  chaco: "Chaco",
  chubut: "Chubut",
  cordoba: "Córdoba",
  corrientes: "Corrientes",
  "entre-rios": "Entre Ríos",
  formosa: "Formosa",
  jujuy: "Jujuy",
  "la-pampa": "La Pampa",
  "la-rioja": "La Rioja",
  mendoza: "Mendoza",
  misiones: "Misiones",
  neuquen: "Neuquén",
  "rio-negro": "Río Negro",
  salta: "Salta",
  "san-juan": "San Juan",
  "san-luis": "San Luis",
  "santa-cruz": "Santa Cruz",
  "santa-fe": "Santa Fe",
  "santiago-del-estero": "Santiago del Estero",
  "tierra-del-fuego": "Tierra del Fuego",
  tucuman: "Tucumán",
} as const;
export type Province = keyof typeof PROVINCES;
export const isProvince = (v: unknown): v is Province => typeof v === "string" && Object.hasOwn(PROVINCES, v);

const plain = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const PROVINCE_ALIASES: Record<string, Province> = {
  caba: "caba",
  "capital federal": "caba",
  capital: "caba",
  "ciudad autonoma de buenos aires": "caba",
  "ciudad de buenos aires": "caba",
  "provincia de buenos aires": "buenos-aires",
  "bs as": "buenos-aires",
  "buenos aires": "buenos-aires",
  pba: "buenos-aires",
  gba: "buenos-aires",
  "tierra del fuego antartida e islas del atlantico sur": "tierra-del-fuego",
};

/** The province code for what someone typed in their profile ("Capital Federal", "Córdoba"), or null. */
export function provinceFromText(text: string | null | undefined): Province | null {
  const t = plain(text ?? "");
  if (!t) return null;
  if (PROVINCE_ALIASES[t]) return PROVINCE_ALIASES[t];
  return (Object.keys(PROVINCES) as Province[]).find((code) => plain(PROVINCES[code]) === t) ?? null;
}

/**
 * What's wrong with a text shown on the board (a note or a nickname): a link (other than
 * pokefans.fun) or contact / payment details. The board is public and has no chat yet: people
 * reach each other by publishing on the market, not by phone.
 */
export function wantTextProblem(text: string): "link" | "contact" | null {
  const t = text.toLowerCase();
  const withoutSite = t.replace(/(https?:\/\/)?(www\.)?pokefans\.fun\S*/g, " ");
  if (/https?:\/\/|www\.|\b[a-z0-9-]+\.(com|ar|net|org|io|me|ly|gg|app|link|shop|store|co|info|bio)\b/.test(withoutSite)) return "link";
  if (/\S+@\S+\.\S+/.test(t) || /(^|\s)@[a-z0-9_.]{2,}/.test(t)) return "contact";
  // A run of 8+ digits (a phone, a CBU), even written with spaces, dots or dashes.
  for (const run of t.match(/\+?\d[\d\s().-]{6,}\d/g) ?? []) if (run.replace(/\D/g, "").length >= 8) return "contact";
  if (/\b(whats ?app|wsp|wpp|telegram|instagram|insta|ig|tel[eé]fono|celular|cel|llamame|escribime|mandame|alias|cbu|cvu|transfer[ií]|transferencia)\b/.test(t)) return "contact";
  return null;
}

/** "Martín R." from a first and last name; "Coleccionista" when there's no name. */
export function defaultAlias(first: string | null | undefined, last: string | null | undefined): string {
  const f = (first ?? "").trim().split(/\s+/)[0] ?? "";
  const l = (last ?? "").trim().charAt(0).toUpperCase();
  if (!f) return "Coleccionista";
  const name = f.charAt(0).toUpperCase() + f.slice(1);
  return l ? `${name} ${l}.` : name;
}

/** One person's "Busco" on the board. Never their user id, email or (unless they show it) their price. */
export interface WantedPost {
  id: number;
  alias: string;
  /** Their shop, when they have an active one (the alias is then its @handle). */
  handle: string | null;
  province: Province | null;
  conditionMin: MarketCondition | null;
  language: string | null;
  quantity: number;
  trade: boolean;
  /** Only when they chose to show it. */
  maxPriceArs: number | null;
  note: string;
  /** When it was published or last moved up. */
  since: string;
}

export interface WantedMarket {
  count: number;
  minPriceArs: number;
}

/** A card on the board: how many people want it, never who. */
export interface WantedCard {
  productId: number;
  slug: string;
  name: string;
  setName: string | null;
  cardNumber: string | null;
  imageKey: string;
  wanting: number;
  /** Someone wanting it takes a trade. */
  trade: boolean;
  provinces: Province[];
  lastAt: string;
  /** On sale in the market now. */
  market: WantedMarket | null;
}

export interface WantedBoard {
  items: WantedCard[];
  total: number;
  page: number;
  pageSize: number;
}

export interface WantedProduct {
  product: { id: number; slug: string; name: string; setName: string | null; cardNumber: string | null; rarity: string | null; imageKey: string; category: string };
  posts: WantedPost[];
  market: WantedMarket | null;
}

/** A card the seller has live that someone wants on the board. */
export interface WantedMatch {
  productId: number;
  slug: string;
  name: string;
  imageKey: string;
  wanting: number;
}

/** "Tenés N cartas publicadas que alguien busca": the numbers for every shop, which cards only with demand alerts (VMAX). */
export interface WantedMatches {
  /** Live cards of the shop someone on the board wants. */
  cards: number;
  /** Different people wanting them. */
  wanting: number;
  /** null: the plan doesn't include demand alerts. */
  items: WantedMatch[] | null;
}

/** One of my own "Busco" (Mi mercado › Busco). */
export interface MyWant {
  id: number;
  productId: number;
  name: string;
  setName: string | null;
  cardNumber: string | null;
  imageKey: string;
  slug: string;
  category: string;
  maxPriceArs: number | null;
  createdAt: string;
  /** On sale now (by others), and from how much. */
  listings: number;
  minPriceArs: number | null;
  /** "Visible en Se busca", as set (see onBoardUntil for whether it's there now). */
  public: boolean;
  conditionMin: MarketCondition | null;
  language: string | null;
  quantity: number;
  trade: boolean;
  showPrice: boolean;
  note: string;
  province: Province | null;
  /** On the board until then; null when it isn't there (private, expired or "conseguida"). */
  onBoardUntil: string | null;
  /** "Subir" can be used from then (null when it's not on the board). */
  bumpFrom: string | null;
  fulfilledAt: string | null;
  /** Taken off the board by pokefans (it stays in the list). */
  hidden: boolean;
}

/** How the signed-in person shows on the board, and what the form starts with. */
export interface WantedProfile {
  alias: string;
  nickname: string | null;
  defaultAlias: string;
  /** Their shop's @handle stands for them while it's active. */
  handle: string | null;
  province: Province | null;
}

export interface WantedFilters {
  q: string;
  trade: boolean;
  province: Province | null;
  priced: boolean;
  sort: WantedSort;
  page: number;
  /** The visitor's game (`juego`, shared/games.ts): the board never mixes games; none means the first open one. */
  game?: string | null;
}

export function wantedFiltersFromQuery(sp: URLSearchParams): WantedFilters {
  const province = sp.get("provincia");
  const sort = sp.get("orden");
  const page = Number(sp.get("pagina"));
  return {
    q: (sp.get("q") ?? "").trim().slice(0, 100),
    trade: sp.get("canje") === "1",
    province: isProvince(province) ? province : null,
    priced: sp.get("precio") === "1",
    sort: sort === "most" ? "most" : "recent",
    page: Number.isInteger(page) && page >= 1 && page <= 100 ? page : 1,
    game: gameSlug(sp.get(GAME_PARAM)),
  };
}

export function wantedFiltersToQuery(f: Partial<WantedFilters>): string {
  const sp = new URLSearchParams();
  if (f.q) sp.set("q", f.q);
  if (f.trade) sp.set("canje", "1");
  if (f.province) sp.set("provincia", f.province);
  if (f.priced) sp.set("precio", "1");
  if (f.sort && f.sort !== "recent") sp.set("orden", f.sort);
  if (f.page && f.page > 1) sp.set("pagina", String(f.page));
  if (f.game) sp.set(GAME_PARAM, f.game);
  return sp.toString();
}
